import React from "react";
import styled from "styled-components";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AiFillTikTok } from "react-icons/ai";
import { FaFacebookF, FaInstagram, FaSnapchatGhost, FaWhatsapp } from "react-icons/fa";
import { useTranslation } from "react-i18next";

const FooterContainer = styled.footer`
  background-color: #f1f1f1;
  padding: 0;
  color: #333;
  font-family: "Arial", sans-serif;
`;

// Footer info section
const InfoSection = styled.div`
  background-color: #f5f5f5;
  padding: 60px 20px 40px;
  display: table;
  width: 100%;
  table-layout: fixed;
  border-spacing: 0 0;
  
  @media (max-width: 992px) {
    padding: 40px 20px;
    display: block;
  }
`;

const HelpSection = styled.div`
  max-width: 300px;

  @media (max-width: 992px) {
    margin-bottom: 30px;
  }
`;

const HelpTitle = styled.h3`
  font-size: 24px;
  margin-bottom: 15px;

  .blue {
    color: #3858f9;
  }
`;

const HelpText = styled.p`
  color: #777;
  margin-bottom: 25px;
`;

const WhatsAppLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  background-color: #25D366;
  color: white;
  border-radius: 50%;
  font-size: 28px;
  transition: all 0.3s ease;
  margin-top: 15px;
  
  &:hover {
    background-color: #128C7E;
    transform: scale(1.1);
  }
`;

const FooterColumn = styled.div`
  display: table-cell;
  vertical-align: top;
  padding: 0 10px;
  position: relative;
  
  @media (max-width: 992px) {
    display: inline-block;
    width: 45%;
    margin-bottom: 30px;
    vertical-align: top;
  }
  
  @media (max-width: 576px) {
    display: block;
    width: 100%;
  }
`;

const FooterTitle = styled.h4`
  font-weight: bold;
  text-transform: uppercase;
  margin: 0 0 25px 0;
  font-size: 16px;
  height: 20px;
  line-height: 20px;
  display: block;
  padding: 0;
`;

const FooterList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  margin-top: 0;
`;

const FooterListItem = styled.li`
  margin-bottom: 10px;
  font-size: 14px;
`;

const FooterLink = styled(Link)`
  color: #555;
  text-decoration: none;
  transition: color 0.3s;

  &:hover {
    color: #3858f9;
  }
`;

const OfficeHours = styled.div`
  font-size: 14px;
  color: #555;
  line-height: 1.5;
  margin-top: 0;
`;

const ExternalLink = styled.a`
  color: #555;
  text-decoration: none;
  transition: color 0.3s;
  
  &:hover {
    color: #3858f9;
  }
`;

const SocialMediaList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const SocialMediaItem = styled.li`
  margin-bottom: 12px;
`;

const SocialMediaLink = styled.a`
  display: flex;
  align-items: center;
  color: #555;
  text-decoration: none;
  transition: color 0.3s;
  
  svg {
    margin-right: ${({ dir }) => dir === 'rtl' ? '0' : '10px'};
    margin-left: ${({ dir }) => dir === 'rtl' ? '10px' : '0'};
  }
  
  &:hover {
    color: #3858f9;
  }
`;

// Add a copyright section to the bottom of the footer
const Copyright = styled.div`
  text-align: center;
  padding: 20px 0;
  color: #aaa;
  font-size: 14px;
  background-color: #f0f0f0;
  border-top: 1px solid #e0e0e0;
`;

const Footer = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Updated navigation method to use React Router
  const navigateToPage = (path) => {
    navigate(path);
  };

  return (
    <FooterContainer id="footer">


      {/* Footer Info Section */}
      <InfoSection>
        <HelpSection>
          <HelpTitle>
            <span className="blue">{t('footer.help.question')}</span> {t('footer.help.help')}
          </HelpTitle>
          <HelpText>
            {t('footer.help.text')}
          </HelpText>
          <WhatsAppLink 
            href="https://wa.me/966558507990" 
            target="_blank" 
            rel="noopener noreferrer"
            aria-label="Chat with us on WhatsApp"
          >
            <FaWhatsapp />
          </WhatsAppLink>
        </HelpSection>

        <FooterColumn>
          <FooterTitle>{t('footer.navigation.title')}</FooterTitle>
          <FooterList>
            <FooterListItem>
              <FooterLink as="span" onClick={() => navigateToPage('/')}>
                {t('footer.navigation.home')}
              </FooterLink>
            </FooterListItem>
            <FooterListItem>
              <FooterLink as="span" onClick={() => navigateToPage('/about')}>
                {t('footer.navigation.about')}
              </FooterLink>
            </FooterListItem>
            <FooterListItem>
              <FooterLink as="span" onClick={() => navigateToPage('/services')}>
                {t('footer.navigation.services')}
              </FooterLink>
            </FooterListItem>
            <FooterListItem>
              <FooterLink as="span" onClick={() => navigateToPage('/team')}>
                {t('footer.navigation.team')}
              </FooterLink>
            </FooterListItem>
            <FooterListItem>
              <FooterLink as="span" onClick={() => navigateToPage('/reviews')}>
                {t('footer.navigation.reviews')}
              </FooterLink>
            </FooterListItem>

          </FooterList>
        </FooterColumn>

        <FooterColumn>
          <FooterTitle>{t('footer.contact.title')}</FooterTitle>
          <FooterList>
            <FooterListItem>{t('footer.contact.phone')}</FooterListItem>
            <FooterListItem>
              <ExternalLink href="mailto:national.elite.clinic@gmail.com">
                {t('footer.contact.email')}
              </ExternalLink>
            </FooterListItem>
            <FooterListItem>
              {i18n.language === 'ar' ? t('footer.contact.address.ar') : t('footer.contact.address.en')}
            </FooterListItem>
          </FooterList>
        </FooterColumn>

        <FooterColumn>
          <FooterTitle>{t('footer.social.title')}</FooterTitle>
          <SocialMediaList>
            <SocialMediaItem>
              <SocialMediaLink dir={i18n.dir()} href="https://www.facebook.com/people/%D8%A7%D9%84%D9%86%D8%AE%D8%A8%D8%A9-%D8%A7%D9%84%D9%88%D8%B7%D9%86%D9%8A%D8%A9/pfbid0Zin9PPWJTsVyBwxcK4GAqJy5Q4uj8JSMtuWzj7fHSRaTxXr2jWNrfTzH2UqxNQsHl/?mibextid=wwXIfr&rdid=KeMQPGmIGXR0GFyH&share_url=https%3A%2F%2Fwww.facebook.com%2Fshare%2F16U7iChQ8c%2F%3Fmibextid%3DwwXIfr" target="_blank" rel="noopener noreferrer">
                <FaFacebookF size={24} />
                <span>{t('footer.social.facebook')}</span>
              </SocialMediaLink>
            </SocialMediaItem>
            <SocialMediaItem>
              <SocialMediaLink dir={i18n.dir()} href="https://www.instagram.com/national.elite.medical/?igsh=MTZmdGw3YXVtcjllOA%3D%3D#" target="_blank" rel="noopener noreferrer">
                <FaInstagram size={24} />
                <span>{t('footer.social.instagram')}</span>
              </SocialMediaLink>
            </SocialMediaItem>
            <SocialMediaItem>
              <SocialMediaLink dir={i18n.dir()} href="https://www.tiktok.com/@national_elite?is_from_webapp=1&sender_device=pc" target="_blank" rel="noopener noreferrer">
                <AiFillTikTok size={24} />
                <span>{t('footer.social.tiktok')}</span>
              </SocialMediaLink>
            </SocialMediaItem>
            <SocialMediaItem>
              <SocialMediaLink dir={i18n.dir()} href="https://www.snapchat.com/add/nationalelitecl?sender_web_id=d5c9fc03-6e62-42d8-9ecc-e18623198f37&device_type=desktop&is_copy_url=true" target="_blank" rel="noopener noreferrer">
                <FaSnapchatGhost size={24} />
                <span>{t('footer.social.snapchat')}</span>
              </SocialMediaLink>
            </SocialMediaItem>
          </SocialMediaList>
        </FooterColumn>
        
        <FooterColumn>
          <FooterTitle>{t('footer.hours.title')}</FooterTitle>
          <OfficeHours>
            <div>{i18n.language === 'ar' ? t('footer.hours.weekdays.ar') : t('footer.hours.weekdays.en')}</div>
            <div style={{ marginTop: "8px" }}>{t('footer.hours.timings')}</div>
            
            <div style={{ marginTop: "16px", fontWeight: "bold" }}>
              {i18n.language === 'ar' ? t('footer.hours.ramadan.title.ar') : t('footer.hours.ramadan.title.en')}
            </div>
            <div style={{ marginTop: "4px" }}>{t('footer.hours.ramadan.morning')}</div>
            <div>{t('footer.hours.ramadan.evening')}</div>
          </OfficeHours>
        </FooterColumn>
      </InfoSection>

      <Copyright>
        {t('footer.copyright')}
      </Copyright>
    </FooterContainer>
  );
};

export default Footer;
